/** A muted capture track shorter than this is normal (device hiccup, window switch) and not reported. */
export const STALL_GRACE_MS = 2000;
/** Tracks usually start muted until the first frame arrives, so right after acquisition allow longer. */
export const STARTUP_GRACE_MS = 5000;
/** A second unexpected pause within this window after a resume attempt is reported instead of retried. */
export const PAUSE_RETRY_WINDOW_MS = 10_000;

export interface TrackWatchdogOptions {
  onStall(): void;
  onRecover(): void;
  graceMs?: number;
  startupGraceMs?: number;
}

/**
 * Reports when every live watched track has been `muted` (no frames or samples from the device)
 * for longer than the grace period, and when it recovers.
 */
export class TrackWatchdog {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stalled = false;
  private disposed = false;
  private readonly grace: number;
  private readonly startupGrace: number;

  constructor(
    private readonly tracks: MediaStreamTrack[],
    private readonly opts: TrackWatchdogOptions,
  ) {
    this.grace = opts.graceMs ?? STALL_GRACE_MS;
    this.startupGrace = opts.startupGraceMs ?? STARTUP_GRACE_MS;
    for (const t of tracks) {
      t.addEventListener('mute', this.onMute);
      t.addEventListener('unmute', this.onUnmute);
    }
    if (this.muted()) this.schedule(this.startupGrace);
  }

  isStalled(): boolean {
    return this.stalled;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clear();
    for (const t of this.tracks) {
      t.removeEventListener('mute', this.onMute);
      t.removeEventListener('unmute', this.onUnmute);
    }
  }

  private muted(): boolean {
    const live = this.tracks.filter((t) => t.readyState === 'live');
    return live.length > 0 && live.every((t) => t.muted);
  }

  private schedule(ms: number): void {
    this.clear();
    this.timer = setTimeout(() => {
      this.timer = null;
      if (this.disposed || !this.muted()) return;
      this.stalled = true;
      this.opts.onStall();
    }, ms);
  }

  private clear(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private readonly onMute = (): void => {
    if (this.disposed || this.stalled || this.timer !== null || !this.muted()) return;
    this.schedule(this.grace);
  };

  private readonly onUnmute = (): void => {
    if (this.disposed || this.muted()) return;
    this.clear();
    if (this.stalled) {
      this.stalled = false;
      this.opts.onRecover();
    }
  };
}
